import { Box } from '@zillow/constellation-10';

import { PathsManifest } from '@zg-rentals/paths-manifest';

import SectionLeft from '../SectionLeft';
import SectionRight from '../SectionRight';
import HeaderNavigationLogo from '../Logo';

import { getNavDisplays } from '../../utils/displays';

import { responsiveContainerStyles } from './styles';

import type { Brand, Displays, User, WebSocketSubscribe } from '../../types';

export type Props = Displays & {
  brand: Brand;
  /*
   * userModel
   *
   * the user model
   */
  userModel?: User;
  /*
   * pathname
   *
   * the current path so the link can be highlighted
   */
  pathname: string;
  /*
   * The current host serving the page, defaults to production if not provided
   *
   */
  host?: string;
  /*
   * useRelativePaths
   *
   * Set to true if you want the paths to be relative.
   * Must pass reactRouterLink if set to true.
   */
  useRelativePaths?: boolean;
  /**
   * Overrides the path used when user clicks on the logo.
   */
  logoPathOverride?: string;
  /*
   * for navigating away from client side router
   */
  escapeAllLinks?: boolean;

  /*
   * reactRouterLink
   *
   * pass react-router-dom Link
   * ex: import { Link as reactRouterLink } from 'react-router-dom';
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reactRouterLink: React.ComponentType<React.PropsWithChildren<any>>;

  /*
   * webSocketSource
   * Only consumed by NotificationWebsocketProvider, which AppLayoutContainer mounts once to own
   * the single shared websocket connection for the whole layout. Either:
   *   - an environment-correct websocket URL, which NotificationWebsocketProvider connects to
   *     itself (falling back to a hardcoded per-brand default when omitted), or
   *   - a subscribe function, when the consuming app already owns a websocket connection to the
   *     same backend (e.g. ZRM's RentalManagerWebSocketProvider) and wants
   *     NotificationWebsocketProvider to listen on that connection instead of opening a second one.
   */
  webSocketSource?: string | WebSocketSubscribe;

  /*
   * unreadMessageCount
   *
   * SLW-only override for the top-bar unread messages badge. SLW already owns the
   * count (its satellite getCachedConversationCounts), so it passes it here; the
   * top bar then renders this value and skips its own RMA cachedConversationCounts
   * query, giving the badge a single source instead of a second, redundant fetch.
   * Other apps omit this and keep the RMA-backed default.
   */
  unreadMessageCount?: number;

  /*
   * listingsNavEnabled
   *
   * Overrides the Listings entry point gate. Omit it and the nav resolves the gate itself
   * via useListingCentricViewEnabled (Test B treatment + QA cookie).
   */
  listingsNavEnabled?: boolean;
};

const HeaderNavigation = (props: Props) => {
  const useAbsolutePaths = !props.useRelativePaths;
  const paths = new PathsManifest('/rental-manager', props.brand, useAbsolutePaths, props.host).getAllPaths();

  const { displays } = getNavDisplays(props);

  const {
    altLogo = true,
    pathname,
    reactRouterLink: v4Link,
    userModel = {
      features: {},
    } as User,
    escapeAllLinks = false,
  } = props;

  const helpPage = paths.helpPage;

  // Pass link component from either react-router or next-js
  const Link = v4Link;

  const { base: basePage } = paths;

  const logoLinkPath = props.logoPathOverride || `${basePage}/`;

  return (
    <Box
      data-testid="HeaderNavigationWrapper"
      css={{ background: 'bg.neutral', borderBottom: '1px solid {colors.border.muted}' }}
    >
      <Box css={responsiveContainerStyles}>
        <Box css={{ width: '33%' }}>
          <Box className="HeaderNavigation-sectionLeft">
            <SectionLeft
              displays={displays}
              paths={paths}
              pathname={pathname}
              userModel={userModel}
              useAbsolutePaths={useAbsolutePaths}
              link={Link}
              escapeAllLinks={escapeAllLinks}
            />
          </Box>
        </Box>
        <Box css={{ width: '33%', textAlign: 'center' }}>
          <Box
            data-testid="HeaderNavigation-sectionMiddle-desktop"
            css={
              altLogo ? { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' } : undefined
            }
          >
            <HeaderNavigationLogo to={logoLinkPath} altLogo={altLogo} />
          </Box>
        </Box>
        <Box css={{ width: '33%' }} data-testid="HeaderNavigation-sectionRight">
          <SectionRight
            displays={displays}
            paths={paths}
            helpPage={helpPage || ''}
            pathname={pathname}
            userModel={userModel}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HeaderNavigation;
